import React, { useEffect, useRef } from 'react';
import { useScrollReveal } from '../hooks/useScrollReveal';
import { ArrowDown, Zap } from 'lucide-react';

function FloatingParticles() {
  const particles = Array.from({ length: 20 }, (_, i) => ({
    id: i,
    left: `${Math.random() * 100}%`,
    delay: `${Math.random() * 6}s`,
    duration: `${4 + Math.random() * 4}s`,
    size: Math.random() > 0.5 ? 2 : 1
  }));

  return (
    <div className="hero-particles">
      {particles.map((p) =>
      <div
        key={p.id}
        className="particle"
        style={{
          left: p.left,
          width: `${p.size}px`,
          height: `${p.size}px`,
          animationDelay: p.delay,
          animationDuration: p.duration
        }} />

      )}
    </div>);

}

function AnimatedCounter({ value, label }) {
  const ref = useRef(null);
  const [counterRef, isVisible] = useScrollReveal({ threshold: 0.5 });

  return (
    <div ref={counterRef} className="text-center md:text-left">
      <div className={`text-2xl md:text-3xl font-black text-emerald-400 transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
        {value}
      </div>
      <div className="font-mono text-[10px] tracking-[0.2em] text-white/30 mt-1">{label}</div>
    </div>);

}

export default function HeroSection() {
  const [engineeringRef, engineeringVisible] = useScrollReveal({ threshold: 0.2 });

  const scrollToWork = () => {
    const el = document.querySelector('#work');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  const scrollToContact = () => {
    const el = document.querySelector('#contact');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <>
      {/* Hero Section */}
      <section className="relative min-h-screen flex flex-col justify-center overflow-hidden bg-[#050505]">
        {/* Dynamic Background */}
        <div className="hero-grid-bg" />
        <div className="hero-glow-1" />
        <div className="hero-glow-2" />
        <div className="hero-glow-3" />
        <FloatingParticles />
        <div className="hero-scanline" />

        {/* Hero Content */}
        <div className="relative z-[2] max-w-[1440px] mx-auto w-full px-6 md:px-10 pt-28 md:pt-32">
          {/* Status badge */}
          <div className="hero-fade-1 flex items-center gap-2 mb-8 ml-0 md:ml-14">
            <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
            <span className="font-mono text-[10px] tracking-[0.3em] text-emerald-500/70 uppercase">
              iGaming Affiliate Network — Active
            </span>
          </div>

          <div className="flex items-start">
            <div className="flex-1 max-w-[75%]">
              {/* Line 1: GO2 */}
              <div className="hero-fade-2 flex items-center gap-4 md:gap-6">
                <span className="hidden md:block font-mono text-[10px] tracking-[0.2em] text-emerald-500/30 whitespace-nowrap -ml-2">/// 01</span>
                <h1 className="hero-title hero-text-glow leading-[0.85] tracking-[-0.04em]">
                  GO2
                </h1>
              </div>
              {/* Line 2: MEDIA */}
              <div className="hero-fade-3 flex items-center gap-4 md:gap-6">
                <span className="hidden md:block font-mono text-[10px] tracking-[0.2em] text-emerald-500/30 whitespace-nowrap -ml-2">/// 02</span>
                <h1 className="hero-title leading-[0.85] tracking-[-0.04em]">
                  <span className="hero-title-accent">MEDIA</span>
                </h1>
              </div>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="hero-fade-4 flex flex-wrap items-center gap-4 mt-8 md:mt-12 ml-0 md:ml-14">
            <button
              onClick={scrollToContact}
              className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase px-8 py-4 md:px-10 md:py-5 bg-emerald-500 text-black font-bold rounded-full hover:bg-emerald-400 hover:shadow-[0_0_30px_rgba(16,185,129,0.3)] transition-all duration-300">

              Partner With Us
            </button>
            <button
              onClick={scrollToWork}
              className="font-mono text-xs md:text-sm tracking-[0.2em] uppercase px-8 py-4 md:px-10 md:py-5 bg-white/5 text-white/80 rounded-full border border-white/10 hover:border-emerald-500/40 hover:text-white transition-all duration-300">

              Our Brands
            </button>

            {/* Inline stats */}
            <div className="hidden lg:flex items-center gap-8 ml-8 pl-8 border-l border-white/10">
              <div>
                <span className="text-lg font-black text-emerald-400">5M+</span>
                <span className="font-mono text-[9px] tracking-[0.15em] text-white/30 ml-2">Visits/mo</span>
              </div>
              <div>
                <span className="text-lg font-black text-emerald-400">12</span>
                <span className="font-mono text-[9px] tracking-[0.15em] text-white/30 ml-2">Brands</span>
              </div>
              <div>
                <span className="text-lg font-black text-emerald-400">50+</span>
                <span className="font-mono text-[9px] tracking-[0.15em] text-white/30 ml-2">Operators</span>
              </div>
            </div>
          </div>

          {/* Scroll Indicator */}
          <div className="absolute bottom-10 left-6 md:left-10 flex items-center gap-2">
            <button
              onClick={scrollToWork}
              className="flex items-center gap-2 font-mono text-[10px] tracking-[0.2em] text-white/30 hover:text-emerald-400 transition-colors group">

              <ArrowDown size={12} className="animate-bounce text-emerald-500/50" />
              Scroll
            </button>
          </div>

          {/* Decorative line */}
          <div className="absolute bottom-0 left-0 right-0 h-[1px] hero-line-anim" />
        </div>
      </section>

      {/* Engineering Section */}
      <section
        ref={engineeringRef}
        className="bg-[#050505] py-24 md:py-32 border-t border-white/5">

        <div
          className={`max-w-[1440px] mx-auto px-6 md:px-10 transition-all duration-1000 ${
          engineeringVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`
          }>

          <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-8">
            <div>
              <span className="font-mono text-[10px] tracking-[0.3em] text-emerald-500/50 uppercase flex items-center gap-2">
                <Zap size={12} />
                iGaming Affiliate Network
              </span>
              <h2 className="text-3xl md:text-5xl lg:text-6xl font-black mt-3 tracking-[-0.02em] uppercase text-white">
                Driving Player Value
              </h2>
            </div>
            <div className="max-w-lg">
              <p className="font-mono text-sm leading-relaxed text-white/40">
                We build and operate high-performance iGaming affiliate brands that connect players with the best operators. Data-driven acquisition, premium content, and technology at scale.
              </p>
            </div>
          </div>
        </div>
      </section>
    </>);

}